import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, BUSINESSES } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from '../components/AuthModal';
import HoursDisplay from '../components/HoursDisplay';
import Seo from '../components/Seo';
import site from '../content/site';
import { DISCOUNTS_ENABLED, VIDEOS_ENABLED } from '../content/features';
import { ratingSummary } from '../utils/rating';
import ReviewSection from '../components/reviews/ReviewSection';
import VideoShelf from '../components/videos/VideoShelf';
import SocialLinks from '../components/SocialLinks';
import { ClaimBadge } from '../components/ClaimRestaurant';
import './RestaurantPage.css';

// People come here for the reviews, so those start within the first screen.
// Everything about the venue itself — photo, bio, address, contact icons and
// hours — sits together in the header above them.
export default function RestaurantPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authModal, setAuthModal] = useState(null);

  const loadRestaurant = useCallback(async () => {
    const snap = await getDoc(doc(db, BUSINESSES, id));
    if (snap.exists()) {
      setRestaurant({ id: snap.id, ...snap.data() });
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    loadRestaurant();
  }, [loadRestaurant]);

  function handleGetDirections() {
    const fullAddress = buildFullAddress(restaurant);
    if (!fullAddress) return;
    const encoded = encodeURIComponent(fullAddress);
    window.open(`https://maps.google.com/?q=${encoded}`, '_blank', 'noopener,noreferrer');
  }

  function handleUseDiscount() {
    // Every signed-up account (Volcyy included) can redeem — no paywall.
    if (!user) {
      setAuthModal('signup');
      return;
    }
    navigate(`/redeem/${id}`);
  }

  if (loading) {
    return (
      <div className="restaurant-page restaurant-page--loading">
        <div className="spinner" aria-label="Loading…" />
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="restaurant-page restaurant-page--not-found">
        <h1>Business not found.</h1>
        <button onClick={() => navigate('/')}>← Back to listings</button>
      </div>
    );
  }

  const {
    name, cuisine, freeDiscount, blackDiscount,
    description, imageUrl, hours, phone, website,
  } = restaurant;

  const fullAddress = buildFullAddress(restaurant);
  const shortAddress = [restaurant.city, restaurant.state].filter(Boolean).join(', ');
  const websiteUrl = normalizeUrl(website);
  const hasFree = DISCOUNTS_ENABLED && Number(freeDiscount) > 0;
  const hasBlack = DISCOUNTS_ENABLED && Number(blackDiscount) > 0;
  const rating = ratingSummary(restaurant);

  // Same helper the prerenderer uses, so a client-side navigation to this
  // listing ends up with exactly the title/description its static HTML has.
  const seo = site.restaurantMeta(restaurant);

  return (
    <>
      <Seo
        title={seo.title}
        description={seo.description}
        canonicalPath={seo.canonicalPath}
        image={imageUrl}
      />
      <main className="restaurant-page">
        <div className="restaurant-page__body">
          <header className="rp-hero">
            <button className="rp-hero__back" onClick={() => navigate(-1)}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M8.5 3L4.5 7l4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back
            </button>

            <div className="rp-hero__top">
              {imageUrl ? (
                <img src={imageUrl} alt={name} className="rp-hero__photo" />
              ) : (
                <div className="rp-hero__photo rp-hero__photo--empty" aria-hidden="true" />
              )}

              <div className="rp-hero__id">
                <span className="rp-hero__cuisine">
                  {cuisine}{shortAddress && <> · {shortAddress}</>}
                </span>
                <h1 className="rp-hero__name">{name}</h1>
                <ClaimBadge restaurant={restaurant} />
                {rating && (
                  <p className="rp-hero__rating" aria-label={rating.label}>
                    {rating.average && (
                      <>
                        <span className="rp-hero__stars" aria-hidden="true">★</span>
                        <strong>{rating.average}</strong>
                      </>
                    )}
                    <span className="rp-hero__count">{rating.countText}</span>
                  </p>
                )}
              </div>

              {(hasFree || hasBlack) && (
                <div className="restaurant-page__discount-badges">
                  {hasFree && (
                    <div className="restaurant-page__discount-badge">
                      <span>{freeDiscount}%</span>
                      <small>Off</small>
                    </div>
                  )}
                  {hasBlack && (
                    <div className="restaurant-page__discount-badge restaurant-page__discount-badge--black">
                      <span>{blackDiscount}%</span>
                      <small>Off</small>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Call, directions, website and the restaurant's own socials, as
                icons: the same four things the full-width buttons did, in one
                row instead of four. */}
            {description && <p className="rp-hero__bio">{description}</p>}
            {fullAddress && (
              <p className="rp-hero__address">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <circle cx="7" cy="6" r="2" stroke="currentColor" strokeWidth="1.2" />
                  <path d="M7 1C4.24 1 2 3.24 2 6c0 3.75 5 7 5 7s5-3.25 5-7c0-2.76-2.24-5-5-5z" stroke="currentColor" strokeWidth="1.2" />
                </svg>
                {fullAddress}
              </p>
            )}
            {restaurant.imageAttribution?.length > 0 && <p className="rp-hero__credit">Photo: {restaurant.imageAttribution.map((author, i) => <React.Fragment key={i}>{i > 0 && ", "}{author.uri && /^https:\/\//.test(author.uri) ? <a href={author.uri} target="_blank" rel="noopener noreferrer">{author.displayName}</a> : author.displayName}</React.Fragment>)} &middot; Google</p>}
            <div className="rp-actions">
              {phone && (
                <a className="rp-icon" href={`tel:${phone}`} title={`Call ${name}`} aria-label={`Call ${name}`}>
                  <svg width="18" height="18" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M2.5 2h2.1l1 2.5-1.2 1.2a8 8 0 003.9 3.9l1.2-1.2L12 9.4V11.5C10.2 12.8 3.1 8.7 2.5 2z" stroke="currentColor" strokeWidth="1.2" />
                  </svg>
                </a>
              )}
              {fullAddress && (
                <button type="button" className="rp-icon" onClick={handleGetDirections} title="Get directions" aria-label={`Get directions to ${name}`}>
                  <svg width="18" height="18" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M12 2L8 12 6 8 2 6 12 2z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
              {websiteUrl && (
                <a className="rp-icon" href={websiteUrl} target="_blank" rel="noopener noreferrer" title="Website" aria-label={`${name} website`}>
                  <svg width="18" height="18" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.2" />
                    <path d="M1.5 7h11M7 1.5c1.6 1.5 2.5 3.4 2.5 5.5S8.6 11 7 12.5C5.4 11 4.5 9.1 4.5 7S5.4 3 7 1.5z" stroke="currentColor" strokeWidth="1.2" />
                  </svg>
                </a>
              )}
              <SocialLinks socials={restaurant.socials} name={name} />
            </div>

            {hours && (
              <div className="rp-hours">
                <HoursDisplay hours={hours} />
              </div>
            )}
          </header>

          <div className="restaurant-page__community" data-nosnippet>
            {VIDEOS_ENABLED && <VideoShelf restaurant={restaurant} />}
            {/* Re-reading the listing after a starred review refreshes the
                average and count in the header above. */}
            <ReviewSection restaurant={restaurant} onRatingChanged={loadRestaurant} />
          </div>

          {(hasFree || hasBlack) && (
            <div className="restaurant-page__actions">
              <button className="restaurant-page__btn restaurant-page__btn--primary" onClick={handleUseDiscount}>
                Use Discount
              </button>
              <p className="restaurant-page__redeem-note">
                This discount can only be redeemed once per day at this venue.
              </p>
            </div>
          )}
        </div>
      </main>

      {authModal && (
        <AuthModal
          defaultTab={authModal}
          heading="Sign in required to use your discount"
          onClose={() => setAuthModal(null)}
          onSuccess={() => navigate(`/redeem/${id}`)}
        />
      )}
    </>
  );
}

function buildFullAddress(restaurant) {
  if (!restaurant) return '';
  const { address, city, state, zipCode, country } = restaurant;
  return [address, city, state, zipCode, country].filter(Boolean).join(', ');
}

// Ensure a stored website value (which may be a bare domain like "example.com")
// is a fully-qualified URL before we use it in an href.
function normalizeUrl(url) {
  const trimmed = (url || '').trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}
